import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  compareActivities,
  countdownText,
  displayText,
  formatDate,
  isDueThisWeek,
  isoWeek,
  makeId,
  phaseColor,
  shortDate,
  today,
} from './helpers';
import { fieldHelp, help } from './helpTexts';
import { cardHoverSx, cardSx, launchColors, pageTitleSx, PillStyle } from './launchTheme';
import { ActivityProjection, LaunchChain, Workspace } from './types';
import type { WorkListFilters } from './ActivityWorkList';
import ActivityRow from './ui/ActivityRow';
import KpiTile from './ui/KpiTile';
import PersonAvatar from './ui/PersonAvatar';
import ProgressRing from './ui/ProgressRing';
import InfoTip from './ui/InfoTip';
import SectionCard from './ui/SectionCard';
import { LaunchStatusPill, StatusPill } from './ui/StatusPill';

export const isReadOnlyLaunch = (workspace: Workspace): boolean => ['closed', 'cancelled'].includes(workspace.launch.status);

export const launchHealth = (workspace: Workspace): (PillStyle & { label: string }) | null => {
  if (!['active', 'paused', 'launched'].includes(workspace.launch.status)) return null;
  const counts = workspace.overview.scheduleCounts;
  if (counts.overdue > 0 || counts.activeBlockers > 0) return { label: 'Försenad', fg: launchColors.dangerText, bg: launchColors.dangerBg };
  if (counts.atRisk > 0) return { label: 'Risk', fg: launchColors.plumDark, bg: launchColors.plumLight };
  return { label: 'I fas', fg: launchColors.mossDark, bg: launchColors.mossLight };
};

export const announcementCountdown = (chain: LaunchChain, asOf: string): string => {
  const week = isoWeek(chain.announcement);
  if (chain.announcement === asOf) return `${chain.name}-avisering idag (v.${week})`;
  return chain.announcement > asOf
    ? `${countdownText(chain.announcement, asOf).replace(/^om /, '')} till ${chain.name}-avisering (v.${week})`
    : `${chain.name}-avisering var ${shortDate(chain.announcement, asOf)} (v.${week})`;
};

const taskCounts = (activities: ActivityProjection[]) => {
  const included = activities.filter((activity) => activity.included);
  const tasks = included.filter((activity) => activity.kind !== 'calendar_marker');
  return {
    done: tasks.filter((activity) => activity.scheduleStatus === 'complete').length,
    tasks: tasks.length,
    markers: included.length - tasks.length,
  };
};

interface HeaderProps {
  workspace: Workspace;
  ownerName: string;
  canAdminister: boolean;
  lifecycleActions: Array<{ type: string; label: string }>;
  onBack: () => void;
  onEdit: () => void;
  onLifecycle: (type: string) => void;
}

export const LaunchHeader: React.FC<HeaderProps> = ({ workspace, ownerName, canAdminister, lifecycleActions, onBack, onEdit, onLifecycle }) => {
  const asOf = workspace.timeline.asOf;
  const health = launchHealth(workspace);
  const chains = [...workspace.chains].sort((left, right) => left.announcement.localeCompare(right.announcement) || left.name.localeCompare(right.name, 'sv'));
  const counts = taskCounts(workspace.activities);
  const readOnly = isReadOnlyLaunch(workspace);
  const products = workspace.launch.products
    .map((product) => (product.articleNumber ? `${product.name} · art.nr ${product.articleNumber}` : product.name))
    .join(', ');
  return (
    <Box component="header" sx={[cardSx, { p: { xs: 2, md: 2.5 } }]}>
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ ml: -1, mb: 0.5, color: launchColors.textSecondary }}>Alla lanseringar</Button>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' }, gap: 2.5, alignItems: 'center' }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography component="h1" sx={[pageTitleSx, { overflowWrap: 'anywhere' }]}>{workspace.launch.name}</Typography>
            <LaunchStatusPill status={workspace.launch.status} size="medium" />
            <InfoTip text={help.headerStatus} />
            {health && <StatusPill label={health.label} fg={health.fg} bg={health.bg} size="medium" />}
            {health && <InfoTip text={help.headerHealth} />}
          </Stack>
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>{products || 'Inga produkter'}</Typography>
              <PersonAvatar name={`${ownerName}, ägare`} size={22} />
            </Stack>
            {chains.length > 0 && !readOnly && (
              <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
                {chains.map((chain) => (
                  <Stack key={chain.id} direction="row" spacing={0.5} alignItems="center">
                    <FlagOutlinedIcon sx={{ fontSize: 18, color: launchColors.petrolDark }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: launchColors.navy }}>{announcementCountdown(chain, asOf)}</Typography>
                  </Stack>
                ))}
                <InfoTip text={help.headerCountdown} />
              </Stack>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ProgressRing value={workspace.launch.progressPercent} />
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{counts.done} av {counts.tasks} uppgifter</Typography>
            {counts.markers > 0 && <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>+ {counts.markers} hållpunkter</Typography>}
            <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>{workspace.launch.progressLabel}</Typography>
          </Box>
          <InfoTip text={help.headerProgress} />
        </Stack>
      </Box>
      {canAdminister && !readOnly && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${launchColors.divider}` }}>
          <InfoTip text={help.headerActions} />
          <Button size="small" startIcon={<EditOutlinedIcon />} onClick={onEdit}>Redigera</Button>
          {lifecycleActions.map((action) => (
            <Button key={action.type} size="small" variant="outlined" onClick={() => onLifecycle(action.type)}>{action.label}</Button>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export const overdueActivationWarning = (count: number): string => (
  `${count} ${count === 1 ? 'uppgift har' : 'uppgifter har'} redan passerat sin deadline – kontrollera aviseringsdatum innan aktivering.`
);

export const LifecycleBanner: React.FC<{ workspace: Workspace; activationSkipped?: boolean }> = ({ workspace, activationSkipped = false }) => {
  const status = workspace.launch.status;
  const overdue = workspace.overview.scheduleCounts.overdue;
  const lastReason = [...workspace.history].reverse().find((event) => event.type === (status === 'paused' ? 'pause' : status === 'closed' ? 'close' : 'cancel'))?.reason;
  if (status === 'cancelled' || status === 'closed') {
    return (
      <Alert severity="info">
        Lanseringen är {status === 'cancelled' ? 'avbruten' : 'avslutad'} och skrivskyddad. Inga åtgärder visas.
        {lastReason ? ` Orsak: ${lastReason}` : ''}
      </Alert>
    );
  }
  if (status === 'paused') {
    return <Alert severity="warning">Lanseringen är pausad.{lastReason ? ` Orsak: ${lastReason}.` : ''} Arbete kan fortfarande registreras.</Alert>;
  }
  if (status === 'draft') {
    return (
      <Alert severity={overdue > 0 ? 'warning' : 'info'}>
        {activationSkipped ? 'Lanseringen skapades som utkast och aktiverades inte. ' : 'Lanseringen är ett utkast. '}
        {overdue > 0 ? overdueActivationWarning(overdue) : 'Aktivera den när planen stämmer.'}
      </Alert>
    );
  }
  return null;
};

interface Props {
  workspace: Workspace;
  isOwner: boolean;
  busy: boolean;
  onOpenActivity: (id: string) => void;
  onLateChange: (command: Record<string, unknown>) => void;
  onShowWork: (patch: Partial<WorkListFilters>) => void;
}

const LaunchOverview: React.FC<Props> = ({ workspace, isOwner, busy, onOpenActivity, onLateChange, onShowWork }) => {
  const asOf = workspace.timeline.asOf;
  const readOnly = isReadOnlyLaunch(workspace);
  const included = useMemo(() => workspace.activities.filter((activity) => activity.included), [workspace.activities]);
  const byId = useMemo(() => new Map(workspace.activities.map((activity) => [activity.id, activity])), [workspace.activities]);
  const attention = useMemo(() => included.filter((activity) => activity.actionRequired).sort(compareActivities), [included]);
  const counts = workspace.overview.scheduleCounts;
  const weekCount = included.filter((activity) => isDueThisWeek(activity, asOf)).length;
  const timelinePoints = workspace.timeline.rows.flatMap((row) => row.points);
  const [lateOpen, setLateOpen] = useState(false);
  const [late, setLate] = useState({ name: '', activityId: '', reason: '', requestedBy: '', impact: '', reworkEstimate: '', occurredAt: today() });

  return (
    <Stack spacing={2}>
      {!readOnly && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
          <KpiTile value={counts.overdue} label="Försenade" fg={launchColors.dangerText} bg={launchColors.dangerBg} accent={launchColors.dangerText} icon={<WarningAmberOutlinedIcon />} info={help.kpiOverdue} onClick={() => onShowWork({ quick: 'overdue' })} />
          <KpiTile value={weekCount} label="Denna vecka" fg={launchColors.warningText} bg={launchColors.warningBg} accent={launchColors.warningMarker} icon={<TodayOutlinedIcon />} info={help.kpiWeek} onClick={() => onShowWork({ quick: 'week' })} />
          <KpiTile value={counts.activeBlockers} label="Blockerade" fg={launchColors.petrolDark} bg="#E7F4F8" accent={launchColors.petrol} icon={<BlockOutlinedIcon />} info={help.kpiBlocked} onClick={() => onShowWork({ quick: 'blocked' })} />
        </Box>
      )}

      <Box component="section" aria-labelledby="launch-phases-heading">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography id="launch-phases-heading" component="h2" sx={{ fontSize: 18, fontWeight: 700, color: launchColors.navy }}>Faser</Typography>
          <InfoTip text={help.overviewPhases} />
          <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>faser överlappar och låser inte varandra</Typography>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
          {workspace.phases.map((phase) => {
            const items = included.filter((activity) => activity.phaseKey === phase.id);
            const lateCount = items.filter((activity) => activity.scheduleStatus === 'overdue').length;
            const next = items.filter((activity) => activity.scheduleStatus !== 'complete' && activity.due).sort((left, right) => String(left.due).localeCompare(String(right.due)))[0];
            const color = phaseColor(phase.id);
            const percent = phase.totalCount ? Math.round((phase.doneCount / phase.totalCount) * 100) : 0;
            return (
              <ButtonBase
                key={phase.id}
                component="div"
                onClick={() => onShowWork({ phase: phase.id })}
                aria-label={`${displayText(phase.name)}: ${phase.doneCount} av ${phase.totalCount} klara${lateCount ? `, ${lateCount} försenade` : ''}. Visa i arbetslistan`}
                sx={[cardSx, cardHoverSx, { display: 'block', textAlign: 'left', p: 1.75, borderTop: `4px solid ${color}` }]}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{displayText(phase.name)}</Typography>
                  <Typography sx={{ fontSize: 12, color: launchColors.textSecondary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{phase.doneCount} av {phase.totalCount}</Typography>
                </Stack>
                <Box aria-hidden sx={{ height: 6, borderRadius: 3, bgcolor: launchColors.neutralBg, my: 1.25 }}>
                  <Box sx={{ width: `${percent}%`, height: 6, borderRadius: 3, bgcolor: color }} />
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: lateCount ? 700 : 400, color: lateCount ? launchColors.dangerText : launchColors.textSecondary }}>
                  {lateCount ? `${lateCount} försenade` : 'Inga förseningar'}
                </Typography>
                <Typography noWrap sx={{ fontSize: 12, color: launchColors.textSecondary, mt: 0.25 }}>
                  {next ? `Nästa: ${displayText(next.title)} · ${shortDate(next.due, asOf)}` : 'Inget öppet med datum'}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {!readOnly && (
        <SectionCard
          title="Att göra nu"
          subtitle="sorterat efter allvar"
          info={help.overviewNow}
          padded={false}
          action={attention.length > 5 ? <Button size="small" onClick={() => onShowWork({})}>Visa alla ({attention.length})</Button> : undefined}
        >
          {attention.length === 0
            ? <Typography sx={{ px: 2, pb: 2, fontSize: 14, color: launchColors.mossDark }}>Inget kräver omedelbar åtgärd.</Typography>
            : (
              <Box role="list" aria-label="Att göra nu">
                {attention.slice(0, 5).map((activity) => (
                  <ActivityRow key={activity.id} compact activity={activity} asOf={asOf} onOpen={() => onOpenActivity(activity.id)} />
                ))}
              </Box>
            )}
        </SectionCard>
      )}

      {workspace.chains.length > 0 && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700, color: launchColors.navy }}>Kedjor</Typography>
          <InfoTip text={help.overviewChains} />
        </Stack>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
        {workspace.chains.map((chain) => {
          const open = timelinePoints.filter((point) => point.labels.some((label) => label.chainId === chain.id) && byId.get(point.id)?.scheduleStatus !== 'complete').length;
          return (
            <Box key={chain.id} component="section" sx={[cardSx, { p: 1.75, bgcolor: launchColors.petrolLight, borderColor: launchColors.petrol }]}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', color: launchColors.petrolDark }}>KEDJA</Typography>
              <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700, color: launchColors.navy }}>{chain.name} · avisering {shortDate(chain.announcement, asOf)}</Typography>
              {(chain.calendarCategories || []).length > 0 && (
                <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>{chain.calendarCategories?.join(' · ')}</Typography>
              )}
              <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>
                Vecka {isoWeek(chain.announcement)} · {countdownText(chain.announcement, asOf)} · {open} öppna krav
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ position: 'relative' }}>
      <Accordion disableGutters sx={[cardSx, { '&:before': { display: 'none' } }]}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="late-changes-content" sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 0.5 } }}>
          <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: launchColors.navy }}>Sena ändringar ({workspace.lateChanges.length})</Typography>
        </AccordionSummary>
        <AccordionDetails id="late-changes-content">
          {isOwner && !readOnly && <Button startIcon={<AddIcon />} onClick={() => setLateOpen(true)} sx={{ mb: 1 }}>Registrera sen ändring</Button>}
          {workspace.lateChanges.length === 0 && <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Inga sena ändringar registrerade.</Typography>}
          {workspace.lateChanges.map((change) => (
            <Box key={change.id} sx={{ py: 1, borderTop: `1px solid ${launchColors.divider}` }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{change.name}</Typography>
              <Typography sx={{ fontSize: 13 }}>{change.impact}</Typography>
              <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>{change.reason} · {formatDate(change.actuallyOccurredAt)}</Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
      <Box sx={{ position: 'absolute', top: 10, right: 48, zIndex: 2 }}>
        <InfoTip text={help.overviewLate} />
      </Box>
      </Box>

      <Dialog open={lateOpen} onClose={() => setLateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Registrera sen ändring</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField required label="Namn" value={late.name} onChange={(event) => setLate((value) => ({ ...value, name: event.target.value }))} />
            <TextField select label="Berörd aktivitet (valfritt)" value={late.activityId} onChange={(event) => setLate((value) => ({ ...value, activityId: event.target.value }))}>
              <MenuItem value="">Hela lanseringen</MenuItem>
              {workspace.activities.map((activity) => <MenuItem key={activity.id} value={activity.id}>{displayText(activity.title)}</MenuItem>)}
            </TextField>
            <TextField required label="Orsak" multiline minRows={2} value={late.reason} onChange={(event) => setLate((value) => ({ ...value, reason: event.target.value }))} />
            <TextField required label="Beställare" helperText={fieldHelp.lateRequestedBy} value={late.requestedBy} onChange={(event) => setLate((value) => ({ ...value, requestedBy: event.target.value }))} />
            <TextField required label="Påverkan" helperText={fieldHelp.lateImpact} multiline minRows={2} value={late.impact} onChange={(event) => setLate((value) => ({ ...value, impact: event.target.value }))} />
            <TextField label="Uppskattad omarbetning" helperText={fieldHelp.lateRework} value={late.reworkEstimate} onChange={(event) => setLate((value) => ({ ...value, reworkEstimate: event.target.value }))} />
            <TextField type="date" required label="Faktiskt datum" helperText={fieldHelp.lateOccurredAt} InputLabelProps={{ shrink: true }} value={late.occurredAt} onChange={(event) => setLate((value) => ({ ...value, occurredAt: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLateOpen(false)}>Avbryt</Button>
          <Button variant="contained" disabled={busy || !late.name.trim() || !late.reason.trim() || !late.requestedBy.trim() || !late.impact.trim()} onClick={() => {
            onLateChange({
              type: 'recordLateChange',
              id: makeId('late'),
              name: late.name.trim(),
              activityId: late.activityId || null,
              reason: late.reason.trim(),
              requestedBy: late.requestedBy.trim(),
              impact: late.impact.trim(),
              reworkEstimate: late.reworkEstimate.trim() || null,
              actuallyOccurredAt: late.occurredAt,
            });
            setLateOpen(false);
          }}>Registrera</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default LaunchOverview;
